import { alpha, createTheme } from "@mui/material";
import { csCZ } from "@mui/x-date-pickers/locales";
import { useCurrentLocation } from "./context/LocationContext";
import { company, getCompanyByHostname } from "shared/src/company.mjs";

const commonThemeOptions = [csCZ];

const themeOptions = {
  cssVariables: true,
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: "#00AFAA",
          dark: "#2F586A",
          light: "#07D0CA",
          contrastText: "#FFFFFF",
          _states: {
            hover: "rgba(5, 131, 127, 0.04)",
            selected: "rgba(5, 131, 127, 0.08)",
            focus: "rgba(5, 131, 127, 0.12)",
            focusVisible: "rgba(5, 131, 127, 0.3)",
            outlinedBorder: "#4F86A0",
            outlinedColor: "#4F86A0",
          },
        },
        secondary: {
          main: "#F8AA13",
          contrastText: "#FFFFFF",
          _states: {
            focusVisible: "rgba(156, 39, 176, 0.3)",
            outlinedBorder: "rgba(156, 39, 176, 0.5)",
          },
        },
        error: {
          main: "#F44771",
          light: "#F44771",
          contrastText: "#FFFFFF",
          indicator: "#E70707",
          _states: {
            focusVisible: "rgba(211, 47, 47, 0.3)",
            outlinedBorder: "rgba(211, 47, 47, 0.5)",
          },
        },
        warning: {
          main: "#F8AA13",
          contrastText: "#FFFFFF",
          indicator: "#F2A32B",
          dark: "#E65100",
          _states: {
            focusVisible: "rgba(239, 108, 0, 0.3)",
            outlinedBorder: "rgba(239, 108, 0, 0.5)",
          },
        },
        info: {
          main: "#00AFAA",
          dark: "#08908C",
          light: "#00AFAA",
          contrastText: "#FFFFFF",
          indicator: "#2748FB",
          _states: {
            hover: "rgba(0, 175, 170, 0.04)",
            selected: "rgba(0, 175, 170, 0.08)",
            focusVisible: "rgba(0, 175, 170, 0.3)",
            outlinedBorder: "rgba(0, 175, 170, 0.5)",
          },
        },
        success: {
          main: "#0AC84B",
          light: "#3CE283",
          contrastText: "#FFFFFF",
          indicator: "#0BDB88",
          _states: {
            focusVisible: "rgba(46, 125, 50, 0.3)",
            outlinedBorder: "rgba(46, 125, 50, 0.5)",
          },
        },
        background: {
          default: "#EEF8F8",
          dark: "#244757",
          darker: "#E6E9F9",
          light: "#D0EFED",
        },
        divider: "#C3E4E2",
        elevation: {
          outlined: "#C3E4E2",
        },
        TableCell: {
          border: "#D0EFED",
        },
        _components: {
          alert: {
            error: {
              color: "#FF4757",
              background: "#FFC7CC",
            },
            warning: {
              color: "#F8AA13",
              background: "#FFEBC4",
            },
            success: {
              color: "#0BDB88",
              background: "#C3FFE7",
            },
            info: {
              color: "#2B3674",
            },
          },
          message: {
            color: "#A37BC6",
          },
          switch: {
            knobFillEnabled: "#FAFAFA",
            slideFill: "#000000",
          },
          heading: {
            color: "#244757",
          },
        },
        action: {
          disabled: "#9C9C9C",
          disabledBackground: "#D9D9D9",
        },
        text: {
          primary: "#022A3A",
          _states: {
            focusVisible: "rgba(0, 0, 0, 0.3)",
          },
        },
        common: {
          white_states: {
            main: "#FFFFFF",
            focusVisible: "rgba(255, 255, 255, 0.3)",
          },
        },
      },
      shadows: [
        "none",
        "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
        "0px 15px 12px 0px rgba(0, 0, 0, 0.10)",
        ...Array(24).fill("none"),
      ],
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    htmlFontSize: 10,
    fontFamily: "'Inter var', sans-serif",
    body1: {
      fontSize: "1.2rem",
    },
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      color: "var(--mui-palette-_components-heading-color)",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "var(--mui-palette-_components-heading-color)",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "var(--mui-palette-_components-heading-color)",
    },
    h4: {
      fontSize: "1.3rem",
      fontWeight: 700,
      color: "var(--mui-palette-_components-heading-color)",
    },
    caption: {
      fontSize: "0.8rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          whiteSpace: "pre-line",
          maxWidth: "600px",
          "& a": {
            color: "#fff",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: "small",
      },
      styleOverrides: {
        root: {
          fontWeight: 700,
          textTransform: "none",
          letterSpacing: "-0.02rem",
          pt: 0.4,
          pb: 0.4,
          "&.MuiButton-outlinedPrimary:not(.Mui-disabled)": {
            color: "var(--mui-palette-primary-_states-outlinedColor)",
            borderColor: "var(--mui-palette-primary-_states-outlinedBorder)",
          },
        },
        iconSizeSmall: {
          "& > *:first-child": {
            fontSize: "1.2rem",
          },
        },
        sizeSmall: {
          fontSize: "1.4rem",
          padding: "0.7rem 2.4rem",
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.MuiRadio-root, &.MuiCheckbox-root": {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          aspectRatio: "1 / 1",
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiMenu: {
      defaultProps: {
        PaperProps: {
          elevation: 2,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            elevation: 2,
          },
        },
      },
    },
    MuiTable: {
      defaultProps: {
        stickyHeader: true,
        size: "small",
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: () => ({
          "&:last-child td, &:last-child th": { border: 0 },
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.light,
          color: theme.palette.background.dark,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: "1.2rem",
          variants: [
            {
              props: { variant: "head" },
              style: {
                backgroundColor: theme.palette.background.light,
                color: theme.palette.background.dark,
              },
            },
          ],
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          color: alpha(theme.palette._components.heading.color, 0.32),
          "&.Mui-selected": {
            fontWeight: 600,
            color: "var(--mui-palette-_components-heading-color)",
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          ".MuiTabs-indicator": {
            borderTopLeftRadius: "3px",
            borderTopRightRadius: "3px",
            height: "3px",
          },
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 0,
          paddingTop: theme.spacing(2),
        }),
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
          paddingTop: 0,
        }),
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          "label + &": {
            marginTop: theme.spacing(2.5),
          },
        }),
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          "label + &": {
            marginTop: theme.spacing(2.5),
          },
        }),
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: false,
        size: "small",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          transform: "none",
          color: theme.palette.background.dark,
          "&.Mui-focused": {
            color: theme.palette.background.dark,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: () => ({
          borderRadius: 3,
          "&.Mui-focused": {
            backgroundColor: theme.palette.background.default,
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: theme.palette.divider,
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
            "& legend": { maxWidth: 0 },
          },

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
        }),
      },
      defaultProps: {
        notched: false,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "1.2rem",
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-listbox": {
            paddingBottom: 0,
            paddingTop: 0,
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: () => ({
          borderRadius: 3,
          "&.Mui-focused": {
            backgroundColor: theme.palette.background.default,
            "& .MuiPickersOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: theme.palette.divider,
            },
          },
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
            "& legend": { maxWidth: 0 },
          },

          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
        }),
      },
      defaultProps: {
        notched: false,
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: "h2",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: theme.spacing(2),
          "& .MuiIconButton-root ": {
            color: theme.palette._components.heading.color,
            position: "absolute",
            top: 5,
            right: 5,
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: "flex",
          justifyContent: "space-between",
          padding: theme.spacing(2),
        }),
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          maxHeight: "232px !important", // lower height of timerangepicker dialog to the same as timepicker to avoid overflow outside of the page
        },
      },
    },
    MuiDateRangeCalendar: {
      styleOverrides: {
        root: () => ({
          "--mui-palette-text-secondary": theme.palette.common.white,
          "--mui-palette-text-primary": theme.palette.common.white,
          "--mui-palette-text-disabled": theme.palette.primary.main,
          "--mui-palette-action-disabled": theme.palette.primary.main,
          "& .MuiDateRangeCalendar-monthContainer": {
            backgroundColor: theme.palette.primary.dark,
            borderRadius: theme.shape.borderRadius,
            "&:first-child": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
            "&:last-child": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            },
          },
          "& .MuiPickersArrowSwitcher-root": {
            "--mui-palette-action-active": theme.palette.common.white,
            color: theme.palette.common.white,
          },
          "& .MuiDayCalendar-weekDayLabel, & .MuiDayCalendar-root .MuiButtonBase-root":
            {
              fontSize: "1.2rem",
            },
          "& .MuiDateRangePickerDay-day:focus.Mui-selected": {
            backgroundColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: () => ({
          backgroundColor: theme.palette.common.white,
          alignItems: "center",
          position: "relative",
          borderRadius: 0,
          boxShadow: theme.shadows[1],
          "&.MuiAlert-colorSuccess": {
            color: theme.palette._components.alert.success.color,
            "& .MuiAlert-icon": {
              backgroundColor:
                theme.palette._components.alert.success.background,
            },
          },
          "&.MuiAlert-colorError": {
            color: theme.palette._components.alert.error.color,
            "& .MuiAlert-icon": {
              backgroundColor: theme.palette._components.alert.error.background,
            },
          },
          "&.MuiAlert-colorWarning": {
            color: theme.palette._components.alert.warning.color,
            "& .MuiAlert-icon": {
              backgroundColor:
                theme.palette._components.alert.warning.background,
            },
          },
          "& .MuiAlert-icon": {
            color: "currentColor",
            padding: "1rem",
            borderRadius: "100%",
            fontSize: "20px",
          },
          "& .MuiAlert-action": {
            color: theme.palette.common.black,
            alignSelf: "flex-start",
            paddingTop: 0,
          },
        }),
      },
    },
  },
};

const dentaLabsThemeOptions = {};

const theme = createTheme(themeOptions, ...commonThemeOptions);
const dentaLabsTheme = createTheme(
  themeOptions,
  dentaLabsThemeOptions,
  ...commonThemeOptions,
);

export function useTheme() {
  const currentLocation = useCurrentLocation();

  const companyId =
    currentLocation?.company_id ?? getCompanyByHostname(location.hostname);

  switch (companyId) {
    case company.DENTA_LABS:
      return dentaLabsTheme;
  }

  return theme;
}
